<script setup lang="ts">
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import SearchableSelectMenu from '@/components/ui/searchable-select-menu/SearchableSelectMenu.vue';
import AssistantActionSuggestionCard from '@/components/assistant/AssistantActionSuggestionCard.vue';
import AssistantReplySuggestionCard from '@/components/assistant/AssistantReplySuggestionCard.vue';
import EntitlementUsageProgress from '@/components/subscription/EntitlementUsageProgress.vue';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '@/components/ui/tabs';
import { AlertCircle, Bot, Clipboard, Clock3, FolderKanban, MessageSquareText, PencilLine, RefreshCcw, Send, Sparkles, TriangleAlert } from 'lucide-vue-next';
import { toast } from 'vue-sonner';
import { assistantSuggestionCardDismissals, type AssistantSuggestionApplyInput } from '~/lib/assistantSuggestionUi';
import type { AssistantFeedbackRating } from '~/composables/useAssistantFeedback';
import { matchesOwnedClient } from '~/lib/clientOwnership';
import { formatRelativeDate } from '~/lib/dateFormat';
import type { ThreadSummary } from '~/stores/messages';
import { assistantThreadFingerprint, useAssistantMemory } from '~/composables/useAssistantMemory';
import type { AssistantPromptAnalysis, AssistantPromptMode, AssistantPromptTurn, AssistantSuggestion, AssistantThreadAnalysis } from '~/types/models';

type AssistantFilter =
    | 'all'
    | 'urgent'
    | 'actions'
    | 'replies'
    | 'quotes'
    | 'risks'
    | 'waiting_team'
    | 'waiting_client';

type AssistantMode = 'monitored' | 'prompted';

type SuggestionTopicGroup = {
    key: string;
    label: string;
    actionable: AssistantSuggestion | null;
    reply: AssistantSuggestion | null;
    tasks: AssistantSuggestion[];
    others: AssistantSuggestion[];
};

const props = defineProps<{
    open: boolean;
}>();

const emit = defineEmits<{
    (e: 'update:open', value: boolean): void;
}>();

const route = useRoute();
const runtimeConfig = useRuntimeConfig();
const tenantStore = useTenantStore();
const userStore = useUserStore();
const clientStore = useClientStore();
const messagesStore = useMessagesStore();
const projectStore = useProjectStore();
const { $auth } = useNuxtApp();
const { tenantPath } = useTenantPath();
const {
    buttonLabelForSuggestion,
    copyReplySuggestion,
    applySuggestion,
} = useAssistantActions();
const { submitAssistantFeedback } = useAssistantFeedback();
const {
    usage: aiAllowance,
    loading: aiAllowanceLoading,
    error: aiAllowanceError,
    refresh: refreshAiAllowance,
    setUsage: setAiAllowance,
} = useAiAllowanceSummary({ auto: false });
const {
    getAvailableCachedAnalysis,
    setCachedAnalysis,
    dismissSuggestion,
    resolveSuggestions,
    getHandledMessageIds,
    getPromptSession,
    setPromptSession,
    isSuggestionDismissed,
} = useAssistantMemory();

const analysesByClient = ref<Record<string, AssistantThreadAnalysis>>({});
const loadingByClient = ref<Record<string, boolean>>({});
const errorByClient = ref<Record<string, string>>({});
const applyingSuggestionId = ref('');
const refiningSuggestionId = ref('');
const expandedReplyByClient = ref<Record<string, boolean>>({});
const activeFilter = ref<AssistantFilter>('all');
const assistantMode = ref<AssistantMode>('monitored');
const promptClientId = ref('');
const promptInput = ref('');
const prompting = ref(false);
const promptError = ref('');
const promptTurns = ref<AssistantPromptTurn[]>([]);
const promptResponseMode = ref<AssistantPromptMode>('suggestions');
const promptInputId = 'workspace-assistant-prompt';

const aiAllowancePercent = computed(() => (
    aiAllowance.value && aiAllowance.value.monthlyLimit > 0
        ? Math.min(100, Math.round((aiAllowance.value.monthlyUsed / aiAllowance.value.monthlyLimit) * 100))
        : 0
));
const aiAllowanceLimitReached = computed(() => (
    Boolean(aiAllowance.value && aiAllowance.value.monthlyUsed >= aiAllowance.value.monthlyLimit)
));
const aiAllowanceIndicatorColor = computed(() => {
    if (aiAllowancePercent.value >= 90) return 'var(--destructive)';
    if (aiAllowancePercent.value >= 75) return 'rgb(245 158 11)';
    return 'var(--primary)';
});
const aiAllowanceLabel = computed(() => {
    const allowance = aiAllowance.value;
    if (!allowance) return '';
    return `${allowance.monthlyUsed.toLocaleString()} of ${allowance.monthlyLimit.toLocaleString()} workspace AI actions used · resets ${formatAllowanceReset(allowance.resetsAt)}`;
});
const aiAllowanceEmptyLabel = computed(() => (
    aiAllowanceError.value
        ? 'AI allowance could not be loaded.'
        : 'AI allowance is unavailable for this workspace role.'
));

const suggestionPriority: Record<AssistantSuggestion['kind'], number> = {
    new_project: 0,
    new_task: 1,
    quote_review: 2,
    risk_flag: 3,
    follow_up: 4,
    reply_draft: 5,
};

const greeting = computed(() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
});

const displayName = computed(() =>
    userStore.user?.displayName || userStore.user?.fullName || 'there',
);
const assistantAnalysisMode = computed<'auto' | 'on_request'>(() => (
    tenantStore.currentTenant?.communicationSettings?.assistantMonitoring?.analysisMode
    || (tenantStore.currentTenant?.communicationSettings?.assistantMonitoring?.autoAnalyzeNewThreads ? 'auto' : 'on_request')
));
const shouldAutoAnalyze = computed(() => assistantAnalysisMode.value === 'auto');
const aiChatEnabled = computed(() => (
    runtimeConfig.public.aiChatEnabled === true
    && ['owner', 'admin'].includes(tenantStore.currentTenantRole || '')
));
const analyzedThreadCount = computed(() => recentThreads.value.filter((thread) => Boolean(analysesByClient.value[thread.clientId])).length);

const totalMessages = computed(() =>
    messagesStore.threadSummaries.reduce((sum, thread) => sum + Number(thread.count || 0), 0),
);

const recentThreads = computed(() => {
    const inboundFirst = messagesStore.threadSummaries.filter((thread) => thread.lastDirection === 'client_to_tenant');
    const source = inboundFirst.length > 0 ? inboundFirst : messagesStore.threadSummaries;
    return source.slice(0, 6);
});
const promptClients = computed(() =>
    [...clientStore.clients].sort((a, b) => a.name.localeCompare(b.name)),
);
const promptClientOptions = computed(() => promptClients.value.map(client => ({
    value: client.id,
    label: client.name,
})));
const promptClient = computed(() =>
    promptClients.value.find((client) => client.id === promptClientId.value) || null,
);
const promptNeedsClient = computed(() => promptResponseMode.value === 'suggestions');
const canSubmitPrompt = computed(() =>
    Boolean(promptInput.value.trim())
    && !prompting.value
    && (!promptNeedsClient.value || Boolean(promptClientId.value)),
);
const assistantSummary = computed(() => {
    const channelCounts = messagesStore.workspaceChannelCounts;
    const parts = [
        channelCounts.email ? `${channelCounts.email} email${channelCounts.email === 1 ? '' : 's'}` : '',
        channelCounts.whatsapp ? `${channelCounts.whatsapp} WhatsApp message${channelCounts.whatsapp === 1 ? '' : 's'}` : '',
        channelCounts.app ? `${channelCounts.app} app message${channelCounts.app === 1 ? '' : 's'}` : '',
    ].filter(Boolean);

    const breakdown = parts.length > 0 ? parts.join(', ') : `${totalMessages.value} message${totalMessages.value === 1 ? '' : 's'}`;
    return `${greeting.value}, ${displayName.value}. I can review ${messagesStore.threadSummaries.length} client thread${messagesStore.threadSummaries.length === 1 ? '' : 's'} with ${breakdown} across the workspace inbox.`;
});
const assistantTitle = computed(() => `${greeting.value}, ${displayName.value}.`);
const assistantOverviewStats = computed(() => {
    const channelCounts = messagesStore.workspaceChannelCounts;
    return [
        `${messagesStore.threadSummaries.length} thread${messagesStore.threadSummaries.length === 1 ? '' : 's'}`,
        `${channelCounts.email || 0} email${channelCounts.email === 1 ? '' : 's'}`,
        `${channelCounts.whatsapp || 0} WhatsApp`,
        `${channelCounts.app || 0} app`,
    ];
});

const contextHint = computed(() => {
    if (!shouldAutoAnalyze.value) {
        return 'On request analysis is enabled.';
    }

    if (route.path.endsWith('/messages')) {
        return 'Shared results are reused until a thread changes.';
    }

    return 'Shared results are reused until a thread changes.';
});

function threadFingerprint(thread: ThreadSummary): string {
    return assistantThreadFingerprint(thread.lastAt, Number(thread.count || 0));
}

function formatAllowanceReset(value: string): string {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return 'next month';
    return parsed.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
    });
}

function daysSince(value: unknown): number {
    const parsed = new Date(String(
        typeof value === 'object' && value !== null && 'toDate' in value
            ? (value as { toDate: () => Date }).toDate().toISOString()
            : value || new Date().toISOString(),
    )).getTime();

    if (Number.isNaN(parsed)) return 0;
    return Math.max(0, Math.floor((Date.now() - parsed) / (1000 * 60 * 60 * 24)));
}

function visibleSuggestions(clientId: string, fingerprint: string): AssistantSuggestion[] {
    return [...(analysesByClient.value[clientId]?.suggestions || [])]
        .filter((suggestion) => !isSuggestionDismissed(clientId, fingerprint, suggestion))
        .sort((a, b) => suggestionPriority[a.kind] - suggestionPriority[b.kind]);
}

function actionableSuggestion(clientId: string, fingerprint: string): AssistantSuggestion | null {
    return visibleSuggestions(clientId, fingerprint).find((suggestion) => suggestion.kind !== 'reply_draft') || null;
}

function replySuggestion(clientId: string, fingerprint: string): AssistantSuggestion | null {
    return visibleSuggestions(clientId, fingerprint).find((suggestion) => suggestion.kind === 'reply_draft') || null;
}

function taskSuggestions(clientId: string, fingerprint: string): AssistantSuggestion[] {
    return visibleSuggestions(clientId, fingerprint).filter((suggestion) => suggestion.kind === 'new_task');
}

function taskTitles(clientId: string, fingerprint: string): string[] {
    return taskSuggestions(clientId, fingerprint)
        .map((suggestion) => suggestion.suggestedTaskTitle || suggestion.title)
        .filter(Boolean)
        .slice(0, 4);
}

function uniqueSuggestions(suggestions: AssistantSuggestion[]): AssistantSuggestion[] {
    const seen = new Set<string>();
    return suggestions.filter((suggestion) => {
        if (seen.has(suggestion.id)) return false;
        seen.add(suggestion.id);
        return true;
    });
}

function suggestionsForGroup(group: SuggestionTopicGroup): AssistantSuggestion[] {
    return uniqueSuggestions([
        ...(group.actionable ? [group.actionable] : []),
        ...(group.reply ? [group.reply] : []),
        ...group.tasks,
        ...group.others,
    ]);
}

function suggestionGroupKey(suggestion: AssistantSuggestion): string {
    return suggestion.topicKey || suggestion.sourceMessageIds?.[0] || suggestion.id;
}

function promptVisibleSuggestions(turn: AssistantPromptTurn): AssistantSuggestion[] {
    return [...(turn.analysis?.suggestions || [])].sort((a, b) => suggestionPriority[a.kind] - suggestionPriority[b.kind]);
}

function promptTopicGroups(turn: AssistantPromptTurn): SuggestionTopicGroup[] {
    const groups = new Map<string, SuggestionTopicGroup>();

    for (const suggestion of promptVisibleSuggestions(turn)) {
        const key = suggestionGroupKey(suggestion);
        const existing = groups.get(key) || {
            key,
            label: suggestion.topicLabel || suggestion.suggestedProjectName || suggestion.suggestedTaskTitle || suggestion.title,
            actionable: null,
            reply: null,
            tasks: [],
            others: [],
        };

        if (suggestion.kind === 'reply_draft') {
            if (!existing.reply) existing.reply = suggestion;
        } else if (suggestion.kind === 'new_task') {
            existing.tasks.push(suggestion);
            if (!existing.actionable) {
                existing.actionable = suggestion;
            }
        } else if (!existing.actionable) {
            existing.actionable = suggestion;
        } else {
            existing.others.push(suggestion);
        }

        groups.set(key, existing);
    }

    return [...groups.values()];
}

function promptAnalysisMode(turn: AssistantPromptTurn): AssistantPromptMode {
    return turn.analysis?.mode === 'chat' ? 'chat' : 'suggestions';
}

function threadStatusTokens(thread: ThreadSummary, fingerprint: string): string[] {
    const tokens: string[] = [];
    const action = actionableSuggestion(thread.clientId, fingerprint);
    const waitingDays = daysSince(thread.lastAt);

    if (action?.confidence) {
        tokens.push(`${action.confidence.charAt(0).toUpperCase()}${action.confidence.slice(1)} confidence`);
    } else if (replySuggestion(thread.clientId, fingerprint)?.confidence) {
        const confidence = replySuggestion(thread.clientId, fingerprint)?.confidence || 'low';
        tokens.push(`${confidence.charAt(0).toUpperCase()}${confidence.slice(1)} confidence`);
    }

    if (thread.lastDirection === 'client_to_tenant' && waitingDays > 0) {
        tokens.push(`Client waiting ${waitingDays} day${waitingDays === 1 ? '' : 's'}`);
    } else if (thread.lastDirection === 'tenant_to_client' && waitingDays > 0) {
        tokens.push(`Waiting on client ${waitingDays} day${waitingDays === 1 ? '' : 's'}`);
    }

    if (action?.kind === 'quote_review') {
        tokens.push('Scope change');
    }

    if (action?.kind === 'risk_flag') {
        tokens.push('Urgent');
    }

    return tokens.slice(0, 3);
}

function threadHeadline(thread: ThreadSummary, fingerprint: string): string {
    if (!analysesByClient.value[thread.clientId] && !shouldAutoAnalyze.value) {
        return 'New activity noticed. Ask me to analyze this thread when you want suggestions.';
    }

    const action = actionableSuggestion(thread.clientId, fingerprint);

    if (action?.kind === 'new_project') {
        return 'Client requested additional work. New project likely needed.';
    }

    if (action?.kind === 'new_task') {
        return 'Client request looks ready to turn into a tracked task.';
    }

    if (action?.kind === 'quote_review') {
        return 'Client requested additional scope. Quote revision likely needed.';
    }

    if (action?.kind === 'risk_flag') {
        return 'Timeline or expectation risk detected in this thread.';
    }

    if (replySuggestion(thread.clientId, fingerprint)) {
        return 'Reply draft ready for review.';
    }

    return 'No action needed right now.';
}

async function hydrateCachedRecentThreads() {
    const entries = await Promise.all(recentThreads.value.map(async (thread) => {
        const cached = await getAvailableCachedAnalysis(thread.clientId, threadFingerprint(thread));
        return cached ? [thread.clientId, cached] as const : null;
    }));

    analysesByClient.value = Object.fromEntries(
        entries.filter((entry): entry is readonly [string, AssistantThreadAnalysis] => Boolean(entry)),
    );
}

function hydratePromptSession() {
    const session = getPromptSession();
    promptTurns.value = session.turns || [];
    promptClientId.value = session.clientId || '';
    promptResponseMode.value = session.mode === 'chat' && aiChatEnabled.value
        ? 'chat'
        : 'suggestions';
}

function persistPromptSession() {
    setPromptSession({
        turns: promptTurns.value,
        clientId: promptClientId.value,
        mode: promptResponseMode.value,
    });
}

function clearPromptConversation() {
    promptTurns.value = [];
    promptError.value = '';
    persistPromptSession();
}

function analysisWarnings(clientId: string): string[] {
    return analysesByClient.value[clientId]?.warnings || [];
}

function hasUrgentSignal(thread: ThreadSummary, fingerprint: string): boolean {
    const action = actionableSuggestion(thread.clientId, fingerprint);
    return action?.kind === 'risk_flag'
        || (thread.lastDirection === 'client_to_tenant' && daysSince(thread.lastAt) >= 3);
}

function matchesFilter(thread: ThreadSummary): boolean {
    const fingerprint = threadFingerprint(thread);
    const action = actionableSuggestion(thread.clientId, fingerprint);
    const reply = replySuggestion(thread.clientId, fingerprint);

    if (activeFilter.value === 'all') return true;
    if (activeFilter.value === 'urgent') return hasUrgentSignal(thread, fingerprint);
    if (activeFilter.value === 'actions') return !!action && ['new_project', 'new_task', 'follow_up'].includes(action.kind);
    if (activeFilter.value === 'replies') return !!reply;
    if (activeFilter.value === 'quotes') return action?.kind === 'quote_review';
    if (activeFilter.value === 'risks') return action?.kind === 'risk_flag';
    if (activeFilter.value === 'waiting_team') return thread.lastDirection === 'client_to_tenant';
    if (activeFilter.value === 'waiting_client') return thread.lastDirection === 'tenant_to_client';
    return true;
}

const filteredThreads = computed(() => recentThreads.value.filter(matchesFilter));

const filterOptions = computed(() => {
    const options: Array<{
        id: AssistantFilter;
        label: string;
        count: number;
    }> = [
        { id: 'all', label: 'All', count: recentThreads.value.length },
        { id: 'urgent', label: 'Urgent', count: recentThreads.value.filter((thread) => hasUrgentSignal(thread, threadFingerprint(thread))).length },
        { id: 'actions', label: 'Actions', count: recentThreads.value.filter((thread) => {
            const action = actionableSuggestion(thread.clientId, threadFingerprint(thread));
            return !!action && ['new_project', 'new_task', 'follow_up'].includes(action.kind);
        }).length },
        { id: 'replies', label: 'Replies', count: recentThreads.value.filter((thread) => !!replySuggestion(thread.clientId, threadFingerprint(thread))).length },
        { id: 'quotes', label: 'Quotes', count: recentThreads.value.filter((thread) => actionableSuggestion(thread.clientId, threadFingerprint(thread))?.kind === 'quote_review').length },
        { id: 'risks', label: 'Risks', count: recentThreads.value.filter((thread) => actionableSuggestion(thread.clientId, threadFingerprint(thread))?.kind === 'risk_flag').length },
    ];

    return options;
});

const assistantActivitySummary = computed(() => {
    const urgentCount = recentThreads.value.filter((thread) => hasUrgentSignal(thread, threadFingerprint(thread))).length;
    const replyCount = recentThreads.value.filter((thread) => !!replySuggestion(thread.clientId, threadFingerprint(thread))).length;
    const readyCount = recentThreads.value.filter((thread) => {
        const action = actionableSuggestion(thread.clientId, threadFingerprint(thread));
        return !!action && ['new_project', 'new_task'].includes(action.kind);
    }).length;

    return `${recentThreads.value.length} recent · ${totalMessages.value} msgs · ${urgentCount} urgent · ${replyCount} replies · ${readyCount} ready`;
});

function statusBadgeClass(label: string): string {
    if (label === 'Waiting on team') return 'border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-500/30 dark:bg-slate-500/10 dark:text-slate-200';
    if (label === 'Waiting on client') return 'border-zinc-200 bg-zinc-50 text-zinc-700 dark:border-zinc-500/30 dark:bg-zinc-500/10 dark:text-zinc-200';
    return 'border-border/60 bg-background/70 dark:bg-background/70';
}

function tokenBadgeClass(token: string): string {
    const normalized = token.toLowerCase();

    if (normalized.includes('confidence')) {
        if (normalized.includes('high')) return 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200';
        if (normalized.includes('medium')) return 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-200';
        return 'border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-500/30 dark:bg-slate-500/10 dark:text-slate-200';
    }

    if (normalized.includes('client waiting')) {
        return 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200';
    }

    if (normalized.includes('scope change')) {
        return 'border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-500/30 dark:bg-violet-500/10 dark:text-violet-200';
    }

    if (normalized.includes('urgent')) {
        return 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-200';
    }

    return 'border-border/60 bg-background/70 dark:bg-background/70';
}

function tokenIcon(token: string) {
    const normalized = token.toLowerCase();

    if (normalized.includes('confidence')) return Sparkles;
    if (normalized.includes('client waiting') || normalized.includes('waiting on client')) return Clock3;
    if (normalized.includes('scope change')) return RefreshCcw;
    if (normalized.includes('urgent')) return TriangleAlert;

    return Sparkles;
}

function pendingReplyStorageKey(clientId: string): string {
    return `blossum:assistant:reply:${tenantStore.currentTenantId || 'tenant'}:${clientId}`;
}

async function refreshSummary() {
    if (!tenantStore.currentTenantId) return;
    await Promise.all([
        clientStore.fetchClients(),
        messagesStore.fetchThreadSummaries(),
        projectStore.projects.length === 0 ? projectStore.fetchProjects() : Promise.resolve(),
    ]);
    if (shouldAutoAnalyze.value) {
        await analyzeRecentThreads(true);
        return;
    }

    await hydrateCachedRecentThreads();
}

async function openMessagesInbox() {
    emit('update:open', false);
    await navigateTo(tenantPath('/messages'));
}

async function openThreadInMessages(clientId: string) {
    emit('update:open', false);
    await navigateTo({
        path: tenantPath('/messages'),
        query: { clientId },
    });
}

async function useReplyInInbox(clientId: string, reply: string) {
    if (import.meta.client) {
        window.localStorage.setItem(pendingReplyStorageKey(clientId), reply);
    }

    await openThreadInMessages(clientId);
}

async function analyzeThread(thread: ThreadSummary, force = false) {
    if (!tenantStore.currentTenantId) return;

    const fingerprint = threadFingerprint(thread);
    if (!force) {
        const cached = await getAvailableCachedAnalysis(thread.clientId, fingerprint);
        if (cached) {
            analysesByClient.value = {
                ...analysesByClient.value,
                [thread.clientId]: cached,
            };
            return;
        }
    }

    loadingByClient.value = {
        ...loadingByClient.value,
        [thread.clientId]: true,
    };
    errorByClient.value = {
        ...errorByClient.value,
        [thread.clientId]: '',
    };

    try {
        const token = await $auth.currentUser?.getIdToken();
        if (!token) {
            throw new Error('You must be logged in to use the assistant.');
        }

        const analysis = await $fetch<AssistantThreadAnalysis>('/api/ai/analyze-thread', {
            method: 'POST',
            headers: {
                authorization: `Bearer ${token}`,
            },
            body: {
                tenantId: tenantStore.currentTenantId,
                clientId: thread.clientId,
                handledMessageIds: getHandledMessageIds(thread.clientId, fingerprint),
                threadFingerprint: fingerprint,
            },
        });

        analysesByClient.value = {
            ...analysesByClient.value,
            [thread.clientId]: analysis,
        };
        if (analysis.allowance) setAiAllowance(analysis.allowance);
        setCachedAnalysis(thread.clientId, fingerprint, analysis);
    } catch (error: any) {
        errorByClient.value = {
            ...errorByClient.value,
            [thread.clientId]: error?.data?.statusMessage || error.message || 'Unable to review this thread right now.',
        };
        void refreshAiAllowance();
    } finally {
        loadingByClient.value = {
            ...loadingByClient.value,
            [thread.clientId]: false,
        };
    }
}

async function analyzeRecentThreads(force = false) {
    await Promise.all(recentThreads.value.map((thread) => analyzeThread(thread, force)));
}

async function analyzeThreadsOnRequest() {
    await analyzeRecentThreads(true);
}

function updatePromptTurn(turnId: string, updater: (turn: AssistantPromptTurn) => AssistantPromptTurn) {
    promptTurns.value = promptTurns.value.map((turn) => turn.id === turnId ? updater(turn) : turn);
}

async function submitPrompt() {
    const content = promptInput.value.trim();
    if (!content || !tenantStore.currentTenantId || (promptNeedsClient.value && !promptClient.value)) return;

    promptError.value = '';
    prompting.value = true;

    const userTurn: AssistantPromptTurn = {
        id: `prompt-user-${Date.now()}`,
        role: 'user',
        content,
        clientId: promptClient.value?.id,
        clientName: promptClient.value?.name,
    };

    promptTurns.value = [...promptTurns.value, userTurn];
    persistPromptSession();
    promptInput.value = '';

    try {
        const token = await $auth.currentUser?.getIdToken();
        if (!token) {
            throw new Error('You must be logged in to use the assistant.');
        }

        const analysis = await $fetch<AssistantPromptAnalysis>('/api/ai/prompt-assistant', {
            method: 'POST',
            headers: {
                authorization: `Bearer ${token}`,
            },
            body: {
                tenantId: tenantStore.currentTenantId,
                clientId: promptClient.value?.id,
                mode: promptResponseMode.value,
                prompt: content,
                history: promptTurns.value.slice(-4).map((turn) => ({
                    role: turn.role,
                    content: turn.role === 'assistant'
                        ? turn.analysis?.assistantResponse || turn.content
                        : turn.content,
                })),
            },
        });

        promptTurns.value = [
            ...promptTurns.value,
            {
                id: `prompt-assistant-${Date.now()}`,
                role: 'assistant',
                content: analysis.assistantResponse,
                analysis,
                clientId: promptClient.value?.id,
                clientName: promptClient.value?.name,
            },
        ];
        if (analysis.allowance) setAiAllowance(analysis.allowance);
        persistPromptSession();
    } catch (error: any) {
        promptError.value = error?.data?.statusMessage || error.message || 'Unable to generate prompted suggestions right now.';
        void refreshAiAllowance();
    } finally {
        prompting.value = false;
    }
}

async function applyPromptSuggestion(
    turnId: string,
    suggestion: AssistantSuggestion,
    clientId: string,
    clientName: string,
    applyConfig: AssistantSuggestionApplyInput = {},
    relatedSuggestions?: AssistantSuggestion[],
) {
    applyingSuggestionId.value = suggestion.id;

    try {
        const turn = promptTurns.value.find((item) => item.id === turnId);
        const scopedSuggestions = relatedSuggestions || turn?.analysis?.suggestions || [];
        const selectedTaskSuggestions = Array.isArray(applyConfig.selectedTaskIds)
            ? scopedSuggestions.filter((item) => applyConfig.selectedTaskIds?.includes(item.id))
            : [];

        await applySuggestion({
            suggestion,
            client: {
                id: clientId,
                name: clientName,
            },
            relatedSuggestions: scopedSuggestions,
            projectIdOverride: suggestion.projectId,
            selectedTaskSuggestions,
            applyConfig: {
                ...applyConfig,
                contextKey: `prompt:${turnId}`,
            },
            contextKey: `prompt:${turnId}`,
            onUseReply: async (reply) => {
                await useReplyInInbox(clientId, reply);
            },
        });

        const resolvedIds = new Set(uniqueSuggestions([suggestion, ...selectedTaskSuggestions]).map((item) => item.id));
        updatePromptTurn(turnId, (turn) => ({
            ...turn,
            analysis: turn.analysis
                ? {
                    ...turn.analysis,
                    suggestions: turn.analysis.suggestions.filter((item) => !resolvedIds.has(item.id)),
                }
                : turn.analysis,
        }));
        persistPromptSession();
    } catch (error: any) {
        console.error('Prompted assistant action failed:', error);
        const message = error?.message || 'Unable to apply this assistant suggestion.';
        promptError.value = message;
        toast.error(message);
    } finally {
        applyingSuggestionId.value = '';
    }
}

function dismissPromptSuggestions(turnId: string, suggestions: AssistantSuggestion[]) {
    const dismissedIds = new Set(suggestions.map((suggestion) => suggestion.id));
    if (dismissedIds.size === 0) return;

    updatePromptTurn(turnId, (turn) => ({
        ...turn,
        analysis: turn.analysis
            ? {
                ...turn.analysis,
                suggestions: turn.analysis.suggestions.filter((item) => !dismissedIds.has(item.id)),
            }
            : turn.analysis,
    }));
    persistPromptSession();
}

function dismissPromptSuggestion(turnId: string, suggestion: AssistantSuggestion) {
    dismissPromptSuggestions(turnId, [suggestion]);
}

function dismissPromptActionSuggestion(
    turnId: string,
    suggestion: AssistantSuggestion,
    groupedTasks: AssistantSuggestion[],
) {
    dismissPromptSuggestions(turnId, assistantSuggestionCardDismissals(suggestion, groupedTasks));
}

function dismissSelectedPromptSuggestions(turnId: string, taskIds: string[], relatedSuggestions?: AssistantSuggestion[]) {
    if (taskIds.length === 0) return;

    const turnSuggestions = promptTurns.value
        .find((turn) => turn.id === turnId)
        ?.analysis?.suggestions || [];
    const selected = (relatedSuggestions || turnSuggestions)
        .filter((suggestion) => taskIds.includes(suggestion.id));
    dismissPromptSuggestions(turnId, selected);
}

async function refinePromptSuggestion(turnId: string, suggestion: AssistantSuggestion, instructions: string, projectId?: string, relatedSuggestions?: AssistantSuggestion[]) {
    if (!tenantStore.currentTenantId) return;

    const turn = promptTurns.value.find((item) => item.id === turnId);
    if (!turn?.clientId) return;

    refiningSuggestionId.value = suggestion.id;
    promptError.value = '';

    try {
        const token = await $auth.currentUser?.getIdToken();
        if (!token) {
            throw new Error('You must be logged in to refine a suggestion.');
        }

        const refined = await $fetch<AssistantSuggestion>('/api/ai/refine-suggestion', {
            method: 'POST',
            headers: {
                authorization: `Bearer ${token}`,
            },
            body: {
                tenantId: tenantStore.currentTenantId,
                clientId: turn.clientId,
                suggestion,
                relatedSuggestions: (relatedSuggestions || turn.analysis?.suggestions || []).filter((item) => item.id !== suggestion.id),
                instructions,
                projectId,
            },
        });

        updatePromptTurn(turnId, (currentTurn) => ({
            ...currentTurn,
            analysis: currentTurn.analysis
                ? {
                    ...currentTurn.analysis,
                    generatedAt: new Date().toISOString(),
                    suggestions: currentTurn.analysis.suggestions.map((item) => item.id === suggestion.id ? refined : item),
                }
                : currentTurn.analysis,
        }));
        persistPromptSession();
    } catch (error: any) {
        promptError.value = error?.data?.statusMessage || error.message || 'Unable to refine this prompted suggestion right now.';
    } finally {
        refiningSuggestionId.value = '';
        void refreshAiAllowance();
    }
}

async function buildProjectPlanForPrompt(turnId: string, suggestion: AssistantSuggestion, relatedSuggestions?: AssistantSuggestion[]) {
    await refinePromptSuggestion(
        turnId,
        suggestion,
        'Combine this suggestion with the related task suggestions into a single project plan. Suggest a clear project name and keep the task breakdown aligned to the same topic.',
        undefined,
        relatedSuggestions,
    );
}

async function applyThreadSuggestion(
    clientId: string,
    clientName: string,
    suggestion: AssistantSuggestion,
    applyConfig: AssistantSuggestionApplyInput = {},
) {
    applyingSuggestionId.value = suggestion.id;

    try {
        const relatedSuggestions = analysesByClient.value[clientId]?.suggestions || [];
        const thread = recentThreads.value.find((item) => item.clientId === clientId);
        const selectedTaskSuggestions = Array.isArray(applyConfig.selectedTaskIds)
            ? relatedSuggestions.filter((item) => applyConfig.selectedTaskIds?.includes(item.id))
            : [];
        await applySuggestion({
            suggestion,
            client: {
                id: clientId,
                name: clientName,
            },
            relatedSuggestions,
            projectIdOverride: suggestion.projectId,
            selectedTaskSuggestions,
            applyConfig: {
                ...applyConfig,
                contextKey: `thread:${clientId}:${thread ? threadFingerprint(thread) : 'unknown'}`,
            },
            contextKey: `thread:${clientId}:${thread ? threadFingerprint(thread) : 'unknown'}`,
            onUseReply: async (reply) => {
                await useReplyInInbox(clientId, reply);
            },
        });

        const resolvedSuggestions = uniqueSuggestions([suggestion, ...selectedTaskSuggestions]);
        const fingerprint = thread ? threadFingerprint(thread) : '';
        resolveSuggestions(clientId, fingerprint, resolvedSuggestions, true);
        const updated = analysesByClient.value[clientId];
        if (updated) {
            const resolvedIds = new Set(resolvedSuggestions.map((item) => item.id));
            const next = {
                ...updated,
                suggestions: updated.suggestions.filter((item) => !resolvedIds.has(item.id)),
            };
            analysesByClient.value = {
                ...analysesByClient.value,
                [clientId]: next,
            };
            if (thread) {
                setCachedAnalysis(clientId, fingerprint, next);
            }
        }

        if (applyConfig.leftoverAction === 'dismiss' && Array.isArray(applyConfig.dismissedTaskIds)) {
            if (!thread) return;
            relatedSuggestions
                .filter((item) => applyConfig.dismissedTaskIds?.includes(item.id))
                .forEach((item) => dismissSuggestion(clientId, fingerprint, item));
        }
    } catch (error: any) {
        console.error('Assistant action failed:', error);
        const message = error?.data?.statusMessage || error?.message || 'Unable to apply this assistant suggestion.';
        errorByClient.value = {
            ...errorByClient.value,
            [clientId]: message,
        };
        toast.error(message);
    } finally {
        applyingSuggestionId.value = '';
    }
}

function dismissThreadSuggestions(thread: ThreadSummary, suggestions: AssistantSuggestion[]) {
    const dismissed = uniqueSuggestions(suggestions);
    if (dismissed.length === 0) return;

    const fingerprint = threadFingerprint(thread);
    resolveSuggestions(thread.clientId, fingerprint, dismissed, false);

    const updated = analysesByClient.value[thread.clientId];
    if (!updated) return;

    const dismissedIds = new Set(dismissed.map((suggestion) => suggestion.id));
    const next = {
        ...updated,
        suggestions: updated.suggestions.filter((suggestion) => !dismissedIds.has(suggestion.id)),
    };
    analysesByClient.value = {
        ...analysesByClient.value,
        [thread.clientId]: next,
    };
    setCachedAnalysis(thread.clientId, fingerprint, next);
}

function dismissThreadSuggestion(thread: ThreadSummary, suggestion: AssistantSuggestion) {
    const fingerprint = threadFingerprint(thread);
    dismissThreadSuggestions(
        thread,
        assistantSuggestionCardDismissals(
            suggestion,
            taskSuggestions(thread.clientId, fingerprint),
        ),
    );
}

async function rateThreadSuggestion(
    thread: ThreadSummary,
    suggestion: AssistantSuggestion,
    rating: AssistantFeedbackRating,
) {
    try {
        await submitAssistantFeedback({
            contextKey: `thread:${thread.clientId}:${threadFingerprint(thread)}`,
            suggestion,
            rating,
            surface: 'workspace',
        });
    } catch (error: any) {
        toast.error(error?.data?.statusMessage || error?.message || 'Unable to save feedback.');
    }
}

async function ratePromptSuggestion(
    turnId: string,
    suggestion: AssistantSuggestion,
    rating: AssistantFeedbackRating,
) {
    try {
        await submitAssistantFeedback({
            contextKey: `prompt:${turnId}`,
            suggestion,
            rating,
            surface: 'prompted',
        });
    } catch (error: any) {
        toast.error(error?.data?.statusMessage || error?.message || 'Unable to save feedback.');
    }
}

function dismissSelectedThreadSuggestions(thread: ThreadSummary, taskIds: string[]) {
    if (taskIds.length === 0) return;

    const relatedSuggestions = analysesByClient.value[thread.clientId]?.suggestions || [];
    const selected = relatedSuggestions.filter((suggestion) => taskIds.includes(suggestion.id));
    dismissThreadSuggestions(thread, selected);
}

function toggleReply(clientId: string) {
    expandedReplyByClient.value = {
        ...expandedReplyByClient.value,
        [clientId]: !expandedReplyByClient.value[clientId],
    };
}

function projectOptionsForClient(clientId: string) {
    return projectStore.projects
        .filter((project) => matchesOwnedClient(project, clientId))
        .map((project) => ({ id: project.id, name: project.name }));
}

function promptProjectOptionsForClient(clientId?: string) {
    return projectOptionsForClient(clientId || '');
}

async function refineThreadSuggestion(clientId: string, suggestion: AssistantSuggestion, instructions: string, projectId?: string) {
    if (!tenantStore.currentTenantId) return;

    refiningSuggestionId.value = suggestion.id;
    errorByClient.value = {
        ...errorByClient.value,
        [clientId]: '',
    };

    try {
        const token = await $auth.currentUser?.getIdToken();
        if (!token) {
            throw new Error('You must be logged in to refine a suggestion.');
        }

        const refined = await $fetch<AssistantSuggestion>('/api/ai/refine-suggestion', {
            method: 'POST',
            headers: {
                authorization: `Bearer ${token}`,
            },
            body: {
                tenantId: tenantStore.currentTenantId,
                clientId,
                suggestion,
                relatedSuggestions: (analysesByClient.value[clientId]?.suggestions || []).filter((item) => item.id !== suggestion.id),
                instructions,
                projectId,
            },
        });

        const existing = analysesByClient.value[clientId];
        if (!existing) return;

        const updated = {
            ...existing,
            generatedAt: new Date().toISOString(),
            suggestions: existing.suggestions.map((item) => (
                item.id === suggestion.id ? refined : item
            )),
        };

        analysesByClient.value = {
            ...analysesByClient.value,
            [clientId]: updated,
        };

        const thread = recentThreads.value.find((entry) => entry.clientId === clientId);
        if (thread) {
            setCachedAnalysis(clientId, threadFingerprint(thread), updated);
        }
    } catch (error: any) {
        errorByClient.value = {
            ...errorByClient.value,
            [clientId]: error?.data?.statusMessage || error.message || 'Unable to refine this suggestion right now.',
        };
    } finally {
        refiningSuggestionId.value = '';
        void refreshAiAllowance();
    }
}

async function buildProjectPlanForThread(clientId: string, suggestion: AssistantSuggestion) {
    await refineThreadSuggestion(
        clientId,
        suggestion,
        'Combine this suggestion with the related task suggestions into a single project plan. Suggest a clear project name and keep the task breakdown aligned to the same topic.',
    );
}

watch(
    () => [props.open, tenantStore.currentTenantId] as const,
    async ([open, tenantId]) => {
        if (!open || !tenantId) return;
        await Promise.all([
            clientStore.fetchClients(),
            messagesStore.fetchThreadSummaries(),
            projectStore.projects.length === 0 ? projectStore.fetchProjects() : Promise.resolve(),
            refreshAiAllowance(),
        ]);
        hydratePromptSession();
        if (shouldAutoAnalyze.value) {
            await analyzeRecentThreads(false);
        } else {
            await hydrateCachedRecentThreads();
        }
    },
    { immediate: true },
);

watch(promptClientId, () => {
    promptError.value = '';
});

watch(promptResponseMode, () => {
    promptError.value = '';
    persistPromptSession();
});

watch(promptTurns, () => {
    persistPromptSession();
}, { deep: true });
</script>

<template>
    <Sheet :open="open" @update:open="(value) => emit('update:open', value)">
        <SheetContent side="right" class="flex h-full w-full flex-col border-l bg-card sm:max-w-xl">
            <SheetHeader class="space-y-3 border-b border-border/70 bg-card pb-4">
                <div class="flex items-center gap-2">
                    <Badge variant="secondary" class="gap-1">
                        <Bot class="size-3.5" />
                        Assistant
                    </Badge>
                    <p class="text-sm font-medium text-muted-foreground">
                        {{ tenantStore.currentTenant?.name || 'Workspace' }}
                    </p>
                </div>
                <div class="space-y-2">
                    <SheetTitle class="text-2xl tracking-tight">{{ assistantTitle }}</SheetTitle>
                    <div class="flex flex-wrap gap-2">
                        <Badge
                            v-for="item in assistantOverviewStats"
                            :key="item"
                            variant="outline"
                            class="border-border/60 bg-background/70 text-muted-foreground"
                        >
                            {{ item }}
                        </Badge>
                    </div>
                </div>
                <SheetDescription class="text-sm leading-6">
                    {{ contextHint }}
                </SheetDescription>
            </SheetHeader>

            <Tabs v-model="assistantMode" class="flex min-h-0 flex-1 flex-col">
                <div class="border-b border-border/60 bg-background/60 px-4 py-3 backdrop-blur-sm">
                    <TabsList class="grid w-full grid-cols-2 rounded-2xl bg-muted/70 p-1">
                        <TabsTrigger value="monitored" class="rounded-xl">
                            <MessageSquareText class="size-4" />
                            Monitored
                        </TabsTrigger>
                        <TabsTrigger value="prompted" class="rounded-xl">
                            <PencilLine class="size-4" />
                            Prompted
                        </TabsTrigger>
                    </TabsList>
                </div>

                <TabsContent value="monitored" class="min-h-0 flex-1">
                    <div class="h-full overflow-y-auto p-4">
                        <div class="space-y-6">
                            <section class="space-y-3">
                                <div class="flex items-start justify-between gap-3">
                                    <div>
                                        <h3 class="text-sm font-semibold">Recent client threads</h3>
                                        <p class="mt-1 text-xs text-muted-foreground">
                                            {{ shouldAutoAnalyze
                                                ? assistantActivitySummary
                                                : `${recentThreads.length} recent · ${totalMessages} msgs · ${analyzedThreadCount} reviewed` }}
                                        </p>
                                    </div>
                                    <div class="flex shrink-0 items-center gap-1.5">
                                        <Button size="sm" variant="ghost" class="h-8 px-2.5 text-xs" @click="openMessagesInbox">
                                            Inbox
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            class="h-8 px-3 text-xs"
                                            @click="shouldAutoAnalyze ? refreshSummary() : analyzeThreadsOnRequest()"
                                        >
                                            {{ shouldAutoAnalyze ? 'Refresh' : 'Analyze' }}
                                        </Button>
                                    </div>
                                </div>

                                <div class="-mx-1 overflow-x-auto px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                                    <div class="flex min-w-max gap-2">
                                    <Button
                                        v-for="filter in filterOptions"
                                        :key="filter.id"
                                        size="sm"
                                        :variant="activeFilter === filter.id ? 'default' : 'outline'"
                                        class="h-8 gap-1.5 rounded-xl px-3 text-xs"
                                        :class="activeFilter === filter.id ? 'border-primary/20 bg-accent text-foreground shadow-none' : 'border-border/60 bg-transparent text-muted-foreground shadow-none hover:bg-accent/40 hover:text-foreground'"
                                        @click="activeFilter = filter.id"
                                    >
                                        {{ filter.label }}
                                        <span class="text-[11px] opacity-70">{{ filter.count }}</span>
                                    </Button>
                                    </div>
                                </div>

                                <div v-if="messagesStore.loadingThreads" class="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                                    Loading assistant summary...
                                </div>

                                <div v-else-if="filteredThreads.length === 0" class="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                                    No threads match this filter right now.
                                </div>

                                <div v-else class="space-y-4">
                                    <div
                                        v-for="thread in filteredThreads"
                                        :key="thread.threadId"
                                        class="rounded-[1.4rem] border border-border/70 bg-white p-4 shadow-[0_18px_45px_rgba(15,23,42,0.08)] transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/20 hover:bg-white hover:shadow-[0_22px_52px_rgba(15,23,42,0.14)] dark:bg-card/92 dark:hover:bg-card"
                                    >
                                        <div class="flex items-start justify-between gap-3">
                                            <div class="min-w-0">
                                                <p class="text-base font-semibold tracking-tight text-foreground">{{ thread.clientName }}</p>
                                                <p class="mt-1 text-sm text-foreground/85">{{ thread.lastMessage }}</p>
                                                <p class="mt-2 text-sm text-muted-foreground">{{ threadHeadline(thread, threadFingerprint(thread)) }}</p>
                                            </div>
                                            <div class="flex shrink-0 items-center gap-2">
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    @click="openThreadInMessages(thread.clientId)"
                                                >
                                                    Open Thread
                                                </Button>
                                            </div>
                                        </div>

                                        <div class="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                                            <Badge
                                                variant="outline"
                                                :class="statusBadgeClass(thread.lastDirection === 'client_to_tenant' ? 'Waiting on team' : 'Waiting on client')"
                                            >
                                                <Clock3 class="mr-1 size-3" />
                                                {{ thread.lastDirection === 'client_to_tenant' ? 'Waiting on team' : 'Waiting on client' }}
                                            </Badge>
                                            <Badge variant="outline" class="border-border/60 bg-background/70">
                                                <MessageSquareText class="mr-1 size-3" />
                                                {{ thread.count }} msgs
                                            </Badge>
                                            <Badge variant="outline" class="border-border/60 bg-background/70">
                                                <Clock3 class="mr-1 size-3" />
                                                {{ formatRelativeDate(thread.lastAt) }}
                                            </Badge>
                                            <Badge
                                                v-for="token in threadStatusTokens(thread, threadFingerprint(thread))"
                                                :key="token"
                                                variant="outline"
                                                :class="tokenBadgeClass(token)"
                                            >
                                                <component :is="tokenIcon(token)" class="mr-1 size-3" />
                                                {{ token }}
                                            </Badge>
                                        </div>

                                        <div class="mt-4">
                                            <p v-if="loadingByClient[thread.clientId]" class="text-sm text-muted-foreground">
                                                Reviewing this thread now...
                                            </p>

                                            <p v-else-if="errorByClient[thread.clientId]" class="text-sm text-destructive">
                                                {{ errorByClient[thread.clientId] }}
                                            </p>

                                            <div v-else-if="!analysesByClient[thread.clientId]" class="space-y-3">
                                                <div class="rounded-xl border border-dashed border-border/70 bg-background/55 p-3">
                                                    <p class="text-sm font-medium">Ready to review</p>
                                                    <p class="mt-1 text-sm text-muted-foreground">
                                                        I can see {{ thread.count }} message{{ thread.count === 1 ? '' : 's' }} in this thread. Want me to analyze it and suggest projects, tasks, replies, or risks?
                                                    </p>
                                                    <div class="mt-3">
                                                        <Button size="sm" @click="analyzeThread(thread, true)">
                                                            Analyze Thread
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>

                                            <div v-else class="space-y-3">
                                                <div class="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                                                    <Badge v-if="analysesByClient[thread.clientId]?.cacheStatus === 'hit'" variant="outline">
                                                        Shared cached result
                                                    </Badge>
                                                </div>

                                                <AssistantActionSuggestionCard
                                                    v-if="actionableSuggestion(thread.clientId, threadFingerprint(thread))"
                                                    compact
                                                    :suggestion="actionableSuggestion(thread.clientId, threadFingerprint(thread))!"
                                                    :task-suggestions="taskSuggestions(thread.clientId, threadFingerprint(thread))"
                                                    :applying="applyingSuggestionId === actionableSuggestion(thread.clientId, threadFingerprint(thread))?.id"
                                                    :refining="refiningSuggestionId === actionableSuggestion(thread.clientId, threadFingerprint(thread))?.id"
                                                    :building-project-plan="refiningSuggestionId === actionableSuggestion(thread.clientId, threadFingerprint(thread))?.id"
                                                    :apply-label="buttonLabelForSuggestion(actionableSuggestion(thread.clientId, threadFingerprint(thread))?.kind || 'new_task', taskSuggestions(thread.clientId, threadFingerprint(thread)).length)"
                                                    :project-options="projectOptionsForClient(thread.clientId)"
                                                    can-refine
                                                    :can-build-project-plan="actionableSuggestion(thread.clientId, threadFingerprint(thread))?.kind === 'new_task' || taskSuggestions(thread.clientId, threadFingerprint(thread)).length > 1"
                                                    @dismiss="dismissThreadSuggestion(thread, actionableSuggestion(thread.clientId, threadFingerprint(thread))!)"
                                                    @dismiss-selected="dismissSelectedThreadSuggestions(thread, $event)"
                                                    @apply="applyThreadSuggestion(thread.clientId, thread.clientName, actionableSuggestion(thread.clientId, threadFingerprint(thread))!, $event)"
                                                    @build-project-plan="buildProjectPlanForThread(thread.clientId, actionableSuggestion(thread.clientId, threadFingerprint(thread))!)"
                                                    @refine="refineThreadSuggestion(thread.clientId, actionableSuggestion(thread.clientId, threadFingerprint(thread))!, $event.instructions, $event.projectId)"
                                                    @feedback="rateThreadSuggestion(thread, actionableSuggestion(thread.clientId, threadFingerprint(thread))!, $event)"
                                                />

                                                <AssistantReplySuggestionCard
                                                    v-if="replySuggestion(thread.clientId, threadFingerprint(thread))?.suggestedReply"
                                                    compact
                                                    expandable
                                                    :suggestion="replySuggestion(thread.clientId, threadFingerprint(thread))!"
                                                    :applying="applyingSuggestionId === replySuggestion(thread.clientId, threadFingerprint(thread))?.id"
                                                    :refining="refiningSuggestionId === replySuggestion(thread.clientId, threadFingerprint(thread))?.id"
                                                    :expanded="expandedReplyByClient[thread.clientId]"
                                                    can-refine
                                                    @dismiss="dismissThreadSuggestion(thread, replySuggestion(thread.clientId, threadFingerprint(thread))!)"
                                                    @apply="applyThreadSuggestion(thread.clientId, thread.clientName, replySuggestion(thread.clientId, threadFingerprint(thread))!)"
                                                    @copy="copyReplySuggestion(replySuggestion(thread.clientId, threadFingerprint(thread))?.suggestedReply)"
                                                    @toggle-expand="toggleReply(thread.clientId)"
                                                    @refine="refineThreadSuggestion(thread.clientId, replySuggestion(thread.clientId, threadFingerprint(thread))!, $event.instructions, $event.projectId)"
                                                    @feedback="rateThreadSuggestion(thread, replySuggestion(thread.clientId, threadFingerprint(thread))!, $event)"
                                                />

                                                <div v-if="analysisWarnings(thread.clientId).length > 0" class="space-y-2">
                                                    <p
                                                        v-for="warning in analysisWarnings(thread.clientId)"
                                                        :key="warning"
                                                        class="rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-700"
                                                    >
                                                        {{ warning }}
                                                    </p>
                                                </div>

                                                <p
                                                    v-if="!actionableSuggestion(thread.clientId, threadFingerprint(thread)) && !replySuggestion(thread.clientId, threadFingerprint(thread))"
                                                    class="text-sm text-muted-foreground"
                                                >
                                                    No action needed right now.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="prompted" class="min-h-0 flex-1">
                    <div class="flex h-full min-h-0 flex-col">
                        <div class="border-b border-border/60 bg-background/50 px-4 py-4">
                            <div class="space-y-1">
                                <div class="flex items-center gap-2">
                                    <Badge variant="outline" class="gap-1">
                                        <PencilLine class="size-3.5" />
                                        Prompted Assistant
                                    </Badge>
                                    <Badge v-if="promptClient" variant="outline">{{ promptClient.name }}</Badge>
                                    <Badge variant="outline">{{ promptResponseMode === 'chat' ? 'Chat' : 'Suggestions' }}</Badge>
                                </div>
                                <h3 class="text-sm font-semibold">
                                    {{ promptResponseMode === 'chat' ? 'Talk through work with the assistant' : 'Turn rough notes into structured work' }}
                                </h3>
                                <p class="text-sm text-muted-foreground">
                                    {{ promptResponseMode === 'chat'
                                        ? 'Ask questions, think through messy client asks, or get help shaping the brief before you turn anything into actions.'
                                        : 'Give me a rough brief, a long list, or a messy client request and I’ll turn it into projects, tasks, quote follow-ups, plans, and reply drafts.' }}
                                </p>
                                <div v-if="promptTurns.length > 0">
                                    <Button size="sm" variant="ghost" class="h-8 px-2 text-xs" @click="clearPromptConversation">
                                        Clear conversation
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <div class="min-h-0 flex-1 overflow-y-auto p-4">
                            <div v-if="promptTurns.length === 0" class="rounded-[1.4rem] border border-dashed border-border/70 bg-background/40 p-4 text-sm text-muted-foreground">
                                {{ promptResponseMode === 'chat'
                                    ? 'No assistant chat yet. Start a conversation below and I’ll keep it here so you can come back to it.'
                                    : 'No prompted suggestions yet. Choose a client below, paste in the brief, and I’ll turn it into something structured.' }}
                            </div>

                            <div v-else class="space-y-4">
                                <div
                                    v-for="turn in promptTurns"
                                    :key="turn.id"
                                    class="rounded-[1.4rem] border border-border/70 p-4 shadow-[0_18px_45px_rgba(15,23,42,0.08)]"
                                    :class="turn.role === 'user'
                                        ? 'ml-auto max-w-[84%] bg-accent/18 dark:bg-accent/8'
                                        : 'mr-auto max-w-full bg-white dark:bg-card/92'"
                                >
                                    <div
                                        class="flex items-center gap-2"
                                        :class="turn.role === 'user' ? 'justify-end' : 'justify-between'"
                                    >
                                        <Badge variant="outline" class="gap-1">
                                            <component :is="turn.role === 'assistant' ? Bot : PencilLine" class="size-3.5" />
                                            {{ turn.role === 'assistant' ? 'Assistant' : 'You' }}
                                        </Badge>
                                        <Badge v-if="turn.clientName && turn.role === 'assistant'" variant="outline">{{ turn.clientName }}</Badge>
                                    </div>

                                    <p class="mt-3 whitespace-pre-wrap text-sm text-foreground/90" :class="turn.role === 'user' ? 'text-right' : ''">
                                        {{ turn.role === 'assistant' ? (turn.analysis?.assistantResponse || turn.content) : turn.content }}
                                    </p>

                                    <div v-if="turn.analysis" class="mt-4 space-y-3">
                                        <div class="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                                            <Badge variant="outline">{{ turn.analysis.provider === 'openai' ? 'OpenAI' : 'Rules Fallback' }}</Badge>
                                            <Badge variant="outline">{{ promptAnalysisMode(turn) === 'chat' ? 'Chat' : 'Suggestions' }}</Badge>
                                            <span>Generated {{ formatRelativeDate(turn.analysis.generatedAt) }}</span>
                                            <span v-if="turn.analysis.model">{{ turn.analysis.model }}</span>
                                        </div>

                                        <div v-if="turn.analysis.warnings?.length" class="space-y-2">
                                            <p
                                                v-for="warning in turn.analysis.warnings"
                                                :key="warning"
                                                class="rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-700"
                                            >
                                                {{ warning }}
                                            </p>
                                        </div>

                                        <template v-if="promptAnalysisMode(turn) === 'suggestions'">
                                            <div
                                                v-for="group in promptTopicGroups(turn)"
                                                :key="group.key"
                                                class="space-y-3 border-t border-border/40 pt-3 first:border-t-0 first:pt-0"
                                            >
                                                <div class="flex flex-wrap items-center gap-2">
                                                    <Badge variant="outline">{{ group.label }}</Badge>
                                                    <Badge variant="outline" class="text-xs">{{ group.tasks.length }} task{{ group.tasks.length === 1 ? '' : 's' }}</Badge>
                                                    <Badge v-if="group.reply" variant="outline" class="text-xs">Reply ready</Badge>
                                                </div>

                                                <AssistantActionSuggestionCard
                                                    v-if="group.actionable"
                                                    :suggestion="group.actionable"
                                                    :task-suggestions="group.tasks"
                                                    :applying="applyingSuggestionId === group.actionable.id"
                                                    :refining="refiningSuggestionId === group.actionable.id"
                                                    :building-project-plan="refiningSuggestionId === group.actionable.id"
                                                    :apply-label="buttonLabelForSuggestion(group.actionable.kind || 'new_task', group.tasks.length)"
                                                    :project-options="promptProjectOptionsForClient(turn.clientId)"
                                                    can-refine
                                                    :can-build-project-plan="group.actionable.kind === 'new_task' || group.tasks.length > 1"
                                                    @dismiss="dismissPromptActionSuggestion(turn.id, group.actionable, group.tasks)"
                                                    @dismiss-selected="dismissSelectedPromptSuggestions(turn.id, $event, suggestionsForGroup(group))"
                                                    @apply="applyPromptSuggestion(turn.id, group.actionable, turn.clientId!, turn.clientName || '', $event, suggestionsForGroup(group))"
                                                    @build-project-plan="buildProjectPlanForPrompt(turn.id, group.actionable, suggestionsForGroup(group))"
                                                    @refine="refinePromptSuggestion(turn.id, group.actionable, $event.instructions, $event.projectId, suggestionsForGroup(group))"
                                                    @feedback="ratePromptSuggestion(turn.id, group.actionable, $event)"
                                                />

                                                <AssistantReplySuggestionCard
                                                    v-if="group.reply?.suggestedReply"
                                                    :suggestion="group.reply"
                                                    :applying="applyingSuggestionId === group.reply.id"
                                                    :refining="refiningSuggestionId === group.reply.id"
                                                    can-refine
                                                    @dismiss="dismissPromptSuggestion(turn.id, group.reply)"
                                                    @apply="applyPromptSuggestion(turn.id, group.reply, turn.clientId!, turn.clientName || '', {}, suggestionsForGroup(group))"
                                                    @copy="copyReplySuggestion(group.reply.suggestedReply)"
                                                    @refine="refinePromptSuggestion(turn.id, group.reply, $event.instructions, $event.projectId, suggestionsForGroup(group))"
                                                    @feedback="ratePromptSuggestion(turn.id, group.reply, $event)"
                                                />

                                                <AssistantActionSuggestionCard
                                                    v-for="suggestion in group.others"
                                                    :key="suggestion.id"
                                                    :suggestion="suggestion"
                                                    :task-suggestions="group.tasks"
                                                    :applying="applyingSuggestionId === suggestion.id"
                                                    :refining="refiningSuggestionId === suggestion.id"
                                                    :building-project-plan="refiningSuggestionId === suggestion.id"
                                                    :apply-label="buttonLabelForSuggestion(suggestion.kind, group.tasks.length)"
                                                    :project-options="promptProjectOptionsForClient(turn.clientId)"
                                                    can-refine
                                                    :can-build-project-plan="suggestion.kind === 'new_task' || group.tasks.length > 1"
                                                    collapsible
                                                    default-collapsed
                                                    @dismiss="dismissPromptSuggestion(turn.id, suggestion)"
                                                    @dismiss-selected="dismissSelectedPromptSuggestions(turn.id, $event, suggestionsForGroup(group))"
                                                    @apply="applyPromptSuggestion(turn.id, suggestion, turn.clientId!, turn.clientName || '', $event, suggestionsForGroup(group))"
                                                    @build-project-plan="buildProjectPlanForPrompt(turn.id, suggestion, suggestionsForGroup(group))"
                                                    @refine="refinePromptSuggestion(turn.id, suggestion, $event.instructions, $event.projectId, suggestionsForGroup(group))"
                                                    @feedback="ratePromptSuggestion(turn.id, suggestion, $event)"
                                                />
                                            </div>
                                        </template>

                                        <p
                                            v-if="promptAnalysisMode(turn) === 'suggestions' && turn.analysis.suggestions.length === 0"
                                            class="text-sm text-muted-foreground"
                                        >
                                            No structured suggestions came out of this prompt yet.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="border-t border-border/60 bg-background/88 px-4 py-4 backdrop-blur-sm">
                            <div class="space-y-3">
                                <div class="flex flex-wrap items-center justify-between gap-3">
                                    <div v-if="aiChatEnabled" class="inline-flex rounded-xl border border-border/70 bg-background p-1" role="group" aria-label="Assistant response mode">
                                        <Button
                                            size="sm"
                                            :variant="promptResponseMode === 'chat' ? 'default' : 'ghost'"
                                            :aria-pressed="promptResponseMode === 'chat'"
                                            class="h-8 rounded-lg px-3 text-xs"
                                            @click="promptResponseMode = 'chat'"
                                        >
                                            Chat
                                        </Button>
                                        <Button
                                            size="sm"
                                            :variant="promptResponseMode === 'suggestions' ? 'default' : 'ghost'"
                                            :aria-pressed="promptResponseMode === 'suggestions'"
                                            class="h-8 rounded-lg px-3 text-xs"
                                            @click="promptResponseMode = 'suggestions'"
                                        >
                                            Suggestions
                                        </Button>
                                    </div>
                                    <Badge v-else variant="outline">Structured suggestions</Badge>

                                    <div class="min-w-[220px] flex-1">
                                        <SearchableSelectMenu
                                            v-model="promptClientId"
                                            :options="promptClientOptions"
                                            :placeholder="promptNeedsClient ? 'Choose a client' : 'No client selected'"
                                            search-placeholder="Search clients..."
                                            :allow-clear="!promptNeedsClient"
                                            clear-label="No client selected"
                                            show-all-by-default
                                            variant="form"
                                        />
                                    </div>
                                </div>

                                <div class="space-y-2">
                                    <label :for="promptInputId" class="text-sm font-medium">
                                        {{ promptResponseMode === 'chat' ? 'Ask the assistant' : 'Describe the work to structure' }}
                                    </label>
                                    <textarea
                                        :id="promptInputId"
                                        v-model="promptInput"
                                        rows="4"
                                        :maxlength="2000"
                                        :aria-describedby="`${promptInputId}-help`"
                                        class="w-full rounded-xl border border-border/70 bg-background px-3 py-3 text-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-primary/40"
                                        :placeholder="promptResponseMode === 'chat'
                                            ? 'Example: The client keeps moving the scope around. Can you help me separate what belongs in the existing project, what should be quoted separately, and how I should reply?'
                                            : 'Example: The client wants a new landing page, updates to the onboarding email sequence, and a quote refresh for the brochure work. Please sort this into projects, tasks, and a reply draft.'"
                                    />
                                    <p :id="`${promptInputId}-help`" class="text-xs text-muted-foreground">
                                        {{ promptInput.length }} / 2,000 characters · Generating uses one AI action.
                                    </p>
                                </div>

                                <div class="flex flex-wrap items-center justify-between gap-3">
                                    <p v-if="promptError" class="text-sm text-destructive">
                                        {{ promptError }}
                                    </p>
                                    <p v-else class="text-xs text-muted-foreground">
                                        {{ promptResponseMode === 'chat'
                                            ? 'Chat stays conversational. Switch to suggestions whenever you want the assistant to turn this into structured work.'
                                            : 'A client context keeps structured suggestions directly actionable.' }}
                                    </p>
                                    <Button :disabled="!canSubmitPrompt" @click="submitPrompt">
                                        <Send class="mr-2 size-4" />
                                        {{ prompting ? 'Thinking...' : (promptResponseMode === 'chat' ? 'Send' : 'Generate Suggestions') }}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </TabsContent>
            </Tabs>

            <div class="shrink-0 border-t border-border/60 bg-background/92 px-4 py-3 backdrop-blur-sm">
                <EntitlementUsageProgress
                    compact
                    :label="aiAllowanceLabel"
                    :empty-label="aiAllowanceEmptyLabel"
                    :value="aiAllowancePercent"
                    :loading="aiAllowanceLoading"
                    loading-label="Loading workspace AI allowance…"
                    :limit-reached="aiAllowanceLimitReached"
                    :indicator-color="aiAllowanceIndicatorColor"
                />
            </div>
        </SheetContent>
    </Sheet>
</template>
